import React, { FC } from "react";
import {
  Badge,
  Card,
  Col,
  Divider,
  Row,
  Space,
  Statistic,
  Tag,
  Typography,
} from "antd";
import { DesktopOutlined, UserOutlined } from "@ant-design/icons";
import { UserOverviewItem } from "shared/api";

const { Text, Title } = Typography;

const UserCard: FC<{ item: UserOverviewItem }> = ({ item }) => {
  const {
    user_name,
    user_image_base64,
    user_image_filename,
    open_tasks,
    max_priority,
    tasks_by_machine,
  } = item;

  const machines = Object.entries(tasks_by_machine ?? {});
  const priorityStr =
    max_priority !== null && max_priority !== undefined
      ? String(max_priority).trim()
      : "";

  const imageSrc = user_image_base64
    ? `data:image/${
        user_image_filename?.split(".").pop() ?? "png"
      };base64,${user_image_base64}`
    : null;

  return (
    <Card
      hoverable
      cover={
        <div className="flex h-40 w-full items-center justify-center overflow-hidden">
          {imageSrc ? (
            <img
              alt={user_name}
              className="h-full w-full object-contain"
              src={imageSrc}
            />
          ) : (
            <div className="flex justify-center items-center w-full h-full">
              <UserOutlined className="text-5xl text-neutral-300" />
            </div>
          )}
        </div>
      }
      bodyStyle={{ padding: 16 }}
    >
      <Space direction="vertical" size="middle" className="w-full">
        <Title className="!m-0 truncate" level={5} title={user_name}>
          {user_name}
        </Title>

        <Row gutter={[12, 12]} align="top">
          <Col flex="auto">
            <Statistic
              title="Open Tasks"
              value={open_tasks}
              valueStyle={{ fontSize: 22, fontWeight: 600, lineHeight: 1.2 }}
            />
          </Col>
          {priorityStr && (
            <Col flex="none">
              <Space align="center" size={8} direction="vertical">
                <Text type="secondary" className="text-xs uppercase">
                  Max Priority
                </Text>
                <Tag
                  className="!m-0"
                  color={
                    [
                      "#237804",
                      "#237804",
                      "#237804",
                      "#d46b08",
                      "#d46b08",
                      "#cf1322",
                    ][Number(max_priority)] || "#cf1322"
                  }
                >
                  {priorityStr}
                </Tag>
              </Space>
            </Col>
          )}
        </Row>

        <>
          <Divider className="!my-0" />
          <div>
            <Text
              type="secondary"
              className="mb-2 block text-xs uppercase tracking-wide"
            >
              By Machine
            </Text>
            {machines.length > 0 ? (
              <ul className="m-0 flex max-h-28 list-none flex-col gap-1.5 overflow-y-auto p-0">
                {machines.map(([name, count]) => (
                  <li
                    key={name}
                    className="flex items-center justify-between gap-2"
                  >
                    <span className="flex min-w-0 items-center gap-1.5">
                      <DesktopOutlined className="shrink-0 text-neutral-400" />
                      <Text className="truncate text-sm" title={name}>
                        {name}
                      </Text>
                    </span>
                    <Badge
                      className="shrink-0"
                      color="blue"
                      count={count}
                      showZero
                    />
                  </li>
                ))}
              </ul>
            ) : (
              <Text type="secondary" className="text-sm">
                No machines assigned
              </Text>
            )}
          </div>
        </>
      </Space>
    </Card>
  );
};

export { UserCard };
